import { ApiRequest } from '@/helpers';

export const playlists = {
  namespaced: true,
  state: getInitialState(),
  actions: {
    async fetch({ commit, rootState }, { resetState } = {}) {
      if (resetState) {
        commit('resetState');
      }
      await rootState.organizationsPromise;
      const organizationId = rootState.currentOrganizationId;
      const { data } = await ApiRequest({
        method: 'get',
        url: `/organizations/${organizationId}/playlists`,
      });
      commit('setLoaded', true);
      commit('setPlaylists', data.playlists);
      return data.playlists;
    },
    fetchIfNeeded({ dispatch, state }) {
      if (state.loaded) return;
      return dispatch('fetch');
    },
    async upload(
      { commit },
      { file, organizationId, recordingId, recordingName, playlist, ownerUid, duration }
    ) {
      let formData = new FormData();
      formData.append('file', file);
      const { data } = await ApiRequest({
        method: 'post',
        url: `/organizations/${organizationId}/playlists/upload`,
        data: formData,
        params: {
          recordingId,
          recordingName,
          playlist,
          ownerUid,
          duration,
        },
      });
      commit('merge', { playlists: [data.playlist] });
      return data.playlist;
    },
    async deleteRecording({ commit, rootState }, { playlistId, recordingId }) {
      const organizationId = rootState.currentOrganizationId;
      const { data } = await ApiRequest({
        method: 'delete',
        url: `organizations/${organizationId}/playlists/${playlistId}/${recordingId}`,
      });
      commit('merge', { playlists: [data.playlist] });
      return data.playlist;
    },
    async deletePlaylist({ commit, rootState }, { playlistId }) {
      const organizationId = rootState.currentOrganizationId;
      const { data } = await ApiRequest({
        method: 'delete',
        url: `organizations/${organizationId}/playlists/${playlistId}`,
      });
      commit('removePlaylist', playlistId);
      return data.playlist;
    },
    async updatePlaylist({ commit, rootState }, { id, name }) {
      const organizationId = rootState.currentOrganizationId;
      const { data } = await ApiRequest({
        method: 'put',
        url: `organizations/${organizationId}/playlists/${id}`,
        data: { name },
      });
      commit('rename', { id, name });
      return data.playlist;
    },
    async updatePlaylistRecording({ commit, rootState }, { playlistId, id, name }) {
      const organizationId = rootState.currentOrganizationId;
      const { data } = await ApiRequest({
        method: 'put',
        url: `organizations/${organizationId}/playlists/${playlistId}/${id}`,
        data: { name },
      });
      commit('renamePlaylistRecording', { playlistId, id, name });
      return data.playlist;
    },
  },
  mutations: {
    rename(state, { id, name }) {
      const playlistIndex = state.playlists.findIndex((playlist) => playlist.id === id);
      if (playlistIndex === -1) {
        return;
      }
      state.playlists[playlistIndex].name = name;
    },
    renamePlaylistRecording(state, { playlistId, id, name }) {
      const playlistIndex = state.playlists.findIndex((playlist) => playlist.id === playlistId);

      if (playlistIndex === -1) {
        return;
      }

      const recordingIndex = state.playlists[playlistIndex].recordings.findIndex(
        (recording) => recording.id === id
      );
      if (recordingIndex !== -1) {
        state.playlists[playlistIndex].recordings[recordingIndex].name = name;
      }
    },
    merge(state, { playlists }) {
      const recordingsMap = {};

      playlists.forEach((record) => {
        if (!recordingsMap[record.id]) {
          recordingsMap[record.id] = record;
        }
      });

      // Add state recordings to the map
      state.playlists.forEach((record) => {
        if (!recordingsMap[record.id]) {
          recordingsMap[record.id] = record;
        }
      });

      state.playlists = Object.values(recordingsMap).sort((a, b) => {
        return b.createdAt._seconds - a.createdAt._seconds;
      });
    },
    setPlaylists(state, playlists) {
      state.playlists = playlists;
    },
    removePlaylist(state, playlistId) {
      state.playlists = state.playlists.filter(({ id }) => playlistId !== id);
    },
    setLoaded(state, loaded) {
      state.loaded = loaded;
    },
    resetState(state) {
      Object.assign(state, getInitialState());
    },
  },
  getters: {
    get: (state) => (playlistRecordingId) => {
      return state.playlists.find(({ id }) => playlistRecordingId === id);
    },
    all: (state) => {
      return state.playlists;
    },
  },
};

function getInitialState() {
  return {
    playlists: [],
    loaded: false,
  };
}
